// Un référentiel se gère toujours de la même façon : lister, créer,
// modifier. Ce hook porte cette mécanique une seule fois, pour que les
// cinq onglets des Paramètres ne la réécrivent pas cinq fois — c'est
// ce que faisait AGC, avec cinq comportements différents en cas
// d'erreur.

import { useCallback, useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { useToast } from '../../ui/Toast.jsx';

export function useCrudList({ path, key, label }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);

  // ── LA CHAÎNE DE REQUÊTE N'EST PAS UN SEGMENT DE CHEMIN ──
  //
  // Les onglets listent avec un filtre : `/catalogue/materials?all=true`.
  // La MODIFICATION construisait `${path}/${id}` — donc
  // `/catalogue/materials?all=true/83`, où « 83 » atterrit dans la
  // valeur du paramètre `all` au lieu d'être `:id`. Express ne trouve
  // aucune route PATCH et répond « Route inconnue. » : le message
  // signalé par Charles, mot pour mot.
  //
  // Le défaut était invisible en lecture et en création — les deux
  // portent sur la collection, où la chaîne de requête est légitime.
  // Seule la ligne à modifier tombe, et les Paramètres ne sont pas un
  // écran qu'on ouvre tous les jours.
  const base = path.split('?')[0];
  const load = useCallback(() => api.get(path), [path]);
  const { data, error, loading, reload } = useResource(load);

  const save = useCallback(
    async (form, id) => {
      setPending(true);
      setErrors({});
      try {
        if (id) await api.patch(`${base}/${id}`, form);
        else await api.post(base, form);
        toast.success(id ? `${label} enregistré.` : `${label} créé.`);
        setEditing(null);
        reload();
        return true;
      } catch (err) {
        // Les erreurs par champ viennent du serveur et s'affichent à
        // côté du champ fautif. Le reste part en message général.
        if (err.details) setErrors(err.details);
        else toast.error(err.message);
        return false;
      } finally {
        setPending(false);
      }
    },
    [base, label, reload, toast],
  );

  return {
    rows: data?.[key] ?? [],
    error,
    loading,
    reload,
    editing,
    setEditing,
    errors,
    pending,
    save,
    toast,
  };
}
